import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';
import LookupSelect, { LookupOption } from '../components/LookupSelect';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import { userService, accessRightService, companyService, MODULE_NAMES } from '../services/adminApi';

const TABS = ['User Details', 'App Access', 'Company Access', 'Department Access'] as const;
type Tab = (typeof TABS)[number];

interface LookupItem {
  id: string;
  code: string;
  name: string;
}

const fetchEmployeeOptions = async (search: string): Promise<LookupOption[]> => {
  const res = await userService.lookupEmployees(search);
  return res.data.employees.map((e: any) => ({ value: e.id, label: `${e.full_name} (${e.code})` }));
};

export default function EditUser() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('User Details');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  // User details
  const [email, setEmail] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  const [employeeLabel, setEmployeeLabel] = useState('');
  const [name, setName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [allowMobileLogin, setAllowMobileLogin] = useState(false);
  const [viewOtherSalary, setViewOtherSalary] = useState(false);
  const [lockUnlockAttendance, setLockUnlockAttendance] = useState(false);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  // Password reset
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetResult, setResetResult] = useState<{ newPassword: string; emailed: boolean } | null>(null);

  // App Access
  const [accessRights, setAccessRights] = useState<{ id: string; code: string }[]>([]);
  const [appAccess, setAppAccess] = useState<Record<string, string>>({});

  // Company Access
  const [companies, setCompanies] = useState<{ id: string; name: string }[]>([]);
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);

  // Department Access
  const [departments, setDepartments] = useState<LookupItem[]>([]);
  const [designations, setDesignations] = useState<LookupItem[]>([]);
  const [branches, setBranches] = useState<LookupItem[]>([]);
  const [deptScope, setDeptScope] = useState<
    Record<string, { isSuperAdmin: boolean; isApprover: boolean; departmentIds: string[]; designationIds: string[]; branchIds: string[] }>
  >({});

  useEffect(() => {
    if (!id) return;
    Promise.all([
      userService.get(id),
      accessRightService.list(),
      companyService.list(),
      api.get('/masters/departments'),
      api.get('/masters/designations'),
      api.get('/tms/branches'),
    ])
      .then(([userRes, rightsRes, companiesRes, deptRes, desigRes, branchRes]) => {
        const u = userRes.data.user;
        setEmail(u.email);
        setEmployeeId(u.employee_id || '');
        setEmployeeLabel(u.employee_name ? `${u.employee_name} (${u.employee_code})` : '');
        setName(u.name || '');
        setMobileNumber(u.mobile_number || '');
        setAllowMobileLogin(!!u.allow_mobile_login);
        setViewOtherSalary(!!u.view_other_salary);
        setLockUnlockAttendance(!!u.lock_unlock_attendance);
        setMustChangePassword(!!u.must_change_password);

        setAccessRights(rightsRes.data.accessRights || []);
        const appAccessMap: Record<string, string> = {};
        (userRes.data.appAccess || []).forEach((a: any) => {
          if (a.access_right_id) appAccessMap[a.module_name] = a.access_right_id;
        });
        setAppAccess(appAccessMap);

        setCompanies(companiesRes.data.companies || []);
        setSelectedCompanies(userRes.data.companyAccess || []);

        setDepartments(deptRes.data.departments || []);
        setDesignations(desigRes.data.designations || []);
        setBranches(branchRes.data.branches || []);
        const scopeMap: Record<string, any> = {};
        (userRes.data.departmentAccess || []).forEach((d: any) => {
          scopeMap[d.module_name] = {
            isSuperAdmin: !!d.is_super_admin,
            isApprover: !!d.is_approver,
            departmentIds: d.department_ids || [],
            designationIds: d.designation_ids || [],
            branchIds: d.branch_ids || [],
          };
        });
        setDeptScope(scopeMap);
      })
      .finally(() => setLoading(false));
  }, [id]);

  const scopeFor = (moduleName: string) =>
    deptScope[moduleName] || { isSuperAdmin: false, isApprover: false, departmentIds: [], designationIds: [], branchIds: [] };

  const updateScope = (moduleName: string, patch: Partial<ReturnType<typeof scopeFor>>) => {
    setDeptScope((prev) => ({ ...prev, [moduleName]: { ...scopeFor(moduleName), ...patch } }));
  };

  const toggleInArray = (arr: string[], value: string) =>
    arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

  const performSave = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await userService.update(id, {
        employeeId: employeeId || undefined,
        name: employeeId ? null : name,
        mobileNumber: mobileNumber || null,
        allowMobileLogin,
        viewOtherSalary,
        lockUnlockAttendance,
        appAccess: MODULE_NAMES.map((m) => ({
          moduleName: m,
          accessType: accessRights.find((r) => r.id === appAccess[m])?.code || 'NO ACCESS',
          accessRightId: appAccess[m] || null,
        })),
        companyAccess: selectedCompanies,
        departmentAccess: MODULE_NAMES.map((m) => ({ moduleName: m, ...scopeFor(m) })),
      });
      setConfirmOpen(false);
      navigate('/admin/users');
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update user');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!id) return;
    setResetting(true);
    setResetError('');
    try {
      // No password argument — the backend generates a temporary one and flags
      // the account so the user must replace it at their next login.
      const res = await userService.resetPassword(id);
      setResetResult({ newPassword: res.data.newPassword, emailed: res.data.emailed });
      setMustChangePassword(true);
    } catch (error: any) {
      setResetError(error.response?.data?.message || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const closeReset = () => {
    setResetOpen(false);
    setResetResult(null);
    setResetError('');
  };

  if (loading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Edit User</h1>

      <div className="flex gap-4 border-b mb-6">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`pb-2 text-sm font-medium ${tab === t ? 'border-b-2 border-blue-600 text-blue-600' : 'text-gray-500'}`}
          >
            {t}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-6 max-w-3xl">
        {tab === 'User Details' && (
          <div className="space-y-4">
            <Input label="Email" value={email} disabled />
            {employeeId ? (
              <Input label="Linked Employee" value={employeeLabel} disabled />
            ) : (
              <LookupSelect
                label="Link Employee (leave blank for NONE / external user)"
                value={employeeId}
                displayValue={employeeLabel}
                onChange={(value, opt) => {
                  setEmployeeId(value);
                  setEmployeeLabel(opt ? opt.label : '');
                }}
                fetchOptions={fetchEmployeeOptions}
                placeholder="Search employee..."
              />
            )}
            {!employeeId && (
              <Input label="Name (for external / NONE user)" value={name} onChange={(e) => setName(e.target.value)} />
            )}
            <Input
              label="Mobile Number"
              value={mobileNumber}
              onChange={(e) => setMobileNumber(e.target.value)}
              required={allowMobileLogin}
            />
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={allowMobileLogin} onChange={(e) => setAllowMobileLogin(e.target.checked)} />
              Allow mobile number login (mobile number becomes mandatory)
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={viewOtherSalary} onChange={(e) => setViewOtherSalary(e.target.checked)} />
              View other employees' basic salary
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={lockUnlockAttendance}
                onChange={(e) => setLockUnlockAttendance(e.target.checked)}
              />
              Lock/Unlock TMS attendance transactions
            </label>

            <div className="border-t pt-4 mt-2">
              <h2 className="text-sm font-semibold text-gray-700">Password</h2>
              {mustChangePassword ? (
                <p className="text-sm text-amber-700 mt-1">
                  This user holds a temporary password and will be asked to change it at their next login.
                </p>
              ) : (
                <p className="text-sm text-gray-500 mt-1">
                  Resetting issues a temporary password and forces the user to choose a new one at their next
                  login. Passwords are stored hashed, so the current one cannot be looked up.
                </p>
              )}
              <Button variant="secondary" className="mt-3" onClick={() => setResetOpen(true)}>
                Reset Password
              </Button>
            </div>
          </div>
        )}

        {tab === 'App Access' && (
          <div className="space-y-3">
            <p className="text-sm text-gray-500 mb-2">Assign an Access Right per module (controls what the user can do).</p>
            {MODULE_NAMES.map((m) => (
              <div key={m} className="flex items-center justify-between gap-4">
                <span className="text-sm font-medium w-40">{m}</span>
                <Select
                  value={appAccess[m] || ''}
                  onChange={(e) => setAppAccess({ ...appAccess, [m]: e.target.value })}
                  options={accessRights.map((r) => ({ value: r.id, label: r.code }))}
                  placeholder="NO ACCESS"
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        )}

        {tab === 'Company Access' && (
          <div className="space-y-2">
            <p className="text-sm text-gray-500 mb-2">Select which companies this user has access to.</p>
            {companies.map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={selectedCompanies.includes(c.id)}
                  onChange={() => setSelectedCompanies((prev) => toggleInArray(prev, c.id))}
                />
                {c.name}
              </label>
            ))}
          </div>
        )}

        {tab === 'Department Access' && (
          <div className="space-y-6">
            <p className="text-sm text-gray-500">
              Controls which employee data this user can see per module — Is Super Admin sees everything, Is
              Approver makes them selectable as an approver, otherwise data is scoped to the selected
              departments/designations/branches.
            </p>
            {MODULE_NAMES.map((m) => {
              const scope = scopeFor(m);
              return (
                <div key={m} className="border rounded-lg p-4">
                  <p className="font-semibold text-sm mb-2">{m}</p>
                  <div className="flex gap-6 mb-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={scope.isSuperAdmin}
                        onChange={(e) => updateScope(m, { isSuperAdmin: e.target.checked })}
                      />
                      Is Super Admin
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={scope.isApprover}
                        onChange={(e) => updateScope(m, { isApprover: e.target.checked })}
                      />
                      Is Approver
                    </label>
                  </div>
                  {!scope.isSuperAdmin && (
                    <div className="grid grid-cols-3 gap-4 text-xs">
                      <div>
                        <p className="font-medium mb-1">Departments</p>
                        {departments.map((d) => (
                          <label key={d.id} className="flex items-center gap-1 mb-1">
                            <input
                              type="checkbox"
                              checked={scope.departmentIds.includes(d.id)}
                              onChange={() =>
                                updateScope(m, { departmentIds: toggleInArray(scope.departmentIds, d.id) })
                              }
                            />
                            {d.name}
                          </label>
                        ))}
                      </div>
                      <div>
                        <p className="font-medium mb-1">Designations</p>
                        {designations.map((d) => (
                          <label key={d.id} className="flex items-center gap-1 mb-1">
                            <input
                              type="checkbox"
                              checked={scope.designationIds.includes(d.id)}
                              onChange={() =>
                                updateScope(m, { designationIds: toggleInArray(scope.designationIds, d.id) })
                              }
                            />
                            {d.name}
                          </label>
                        ))}
                      </div>
                      <div>
                        <p className="font-medium mb-1">Branch</p>
                        {branches.map((b) => (
                          <label key={b.id} className="flex items-center gap-1 mb-1">
                            <input
                              type="checkbox"
                              checked={scope.branchIds.includes(b.id)}
                              onChange={() =>
                                updateScope(m, { branchIds: toggleInArray(scope.branchIds, b.id) })
                              }
                            />
                            {b.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => navigate('/admin/users')}>
            Cancel
          </Button>
          <Button onClick={() => setConfirmOpen(true)} loading={saving}>
            Save Changes
          </Button>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Save User"
        message="Save changes to this user's access?"
        confirmLabel="Save"
        variant="primary"
        loading={saving}
        onConfirm={performSave}
        onCancel={() => setConfirmOpen(false)}
      />

      <Modal isOpen={resetOpen} onClose={closeReset} title="Reset Password">
        {resetResult ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              {resetResult.emailed
                ? `A temporary password was emailed to ${email}.`
                : `The email could not be sent — share this temporary password with ${email} directly.`}
            </p>
            <div className="bg-gray-50 border rounded px-4 py-3 font-mono text-lg">{resetResult.newPassword}</div>
            <p className="text-xs text-gray-500">
              They will be asked to choose their own password the next time they log in.
            </p>
            <div className="flex justify-end">
              <Button onClick={closeReset}>Done</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Reset the password for <strong>{email}</strong>? Their current password stops working
              immediately, and they'll be emailed a temporary one to log in with.
            </p>
            {resetError && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
                {resetError}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <Button variant="secondary" onClick={closeReset} disabled={resetting}>
                Cancel
              </Button>
              <Button onClick={handleReset} loading={resetting}>
                Reset Password
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
